import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import Select from "react-select";
import {
  useGetAllBranchesQuery,
} from "../../../Service/Masters";
import useSnackbar from "../../../hooks/digitalMarketing/useSnackbar";
import Toast from "../../../Component/DigitalMarketing/Toast";
import { ConsultantInput } from "./Utils/addConsultantInput";
import {
  useAddConsultantMutation,
  useGetAllLanguagesQuery,
  useUpdateConsultantMutation,
} from "../../../Service/consultant";
import { useSelector } from "react-redux";
import BackdropLoader from "../../../Component/DigitalMarketing/BackDropLoader";

const AddConsultant = (props) => {
  const { refetch, updateData, view, setShow } = props;
  const { data: branchOptions } = useGetAllBranchesQuery({
    refetchOnMountOrArgChange: true,
  });
  const account = useSelector((state: any) => state.auth);
  const { roleName } = account;
  const { data: languageOptions } = useGetAllLanguagesQuery({
    refetchOnMountOrArgChange: true,
  });

  const [selectedLanguages, setSelectedLanguages] = useState(
    updateData?.languagesSpokenId || []
  );

  const [formData, setFormData] = useState({
    branchId: "",
    branchName: "",
    consultantName: "",
    speciality: "",
    email: "",
    revenue: "",
    phone1: "",
    phone2: "",
    languagesOthers: "",
    languagesSpokenId: "",
    languages: "",
    password: "",
  });

  const { open, message, openSnackBar, type } = useSnackbar();
  const [addConsultant, { isLoading: addLoading }] = useAddConsultantMutation();
  const [updateConsultant, { isLoading: updateLoading }] =
    useUpdateConsultantMutation();

  const handleLanguageChange = (selectedOptions) => {
    setSelectedLanguages(selectedOptions);
    setFormData({
      ...formData,
      languagesSpokenId: selectedOptions.map((option) => option.value).join(","),
      languages: selectedOptions.map((option) => option.label).join(","),
    });
  };

  const handleChangeInfo = (e) => {
    let { name, value } = e.target;

    if (name === "phone1" || name === "phone2") {
      value = value.replace(/[^0-9]/g, "");
      value = value.slice(0, 10);
    } else if (name === "email") {
      value = value.replace(/[^a-zA-Z0-9@._-]/g, "");
    } else if (name === "revenue") {
      value = value.replace(/[^0-9]/g, "");
    } else if (name !== "password") {
      value = value.replace(/^[\s]+|[^a-zA-Z0-9 ]/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBranchChange = (e) => {
    const selectedBranch = branchOptions?.data?.find(
      (branch) => branch.id == e.target.value
    );
    setFormData({
      ...formData,
      branchId: selectedBranch?.id,
      branchName: selectedBranch?.branchName || "",
    });
  };

  const handleSubmit = async () => {
    let payload = { ...formData };

    if (updateData?.id) {
      delete payload.password;
    }

    // Required fields (common)
    const requiredFields: Record<string, string> = {
      consultantName: "Consultant Name",
      speciality: "Speciality",
      email: "Email",
      revenue: "Revenue",
      phone1: "Primary Phone",
      languagesSpokenId: "Languages Spoken ID",
    };

    // Add branch fields only for Admin role
    if (roleName !== "branch") {
      requiredFields.branchId = "Branch ID";
      requiredFields.branchName = "Branch Name";
    }

    const missingFields: string[] = [];

    for (const [field, fieldName] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        missingFields.push(fieldName);
      }
    }

    // Special validation for languagesOthers - only required if "Others" selected
    if (
      formData.languagesSpokenId?.includes("10") &&
      !formData.languagesOthers
    ) {
      missingFields.push("Other Languages");
    }

    // Email validation
    if (
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      openSnackBar("Please enter a valid email address", "error");
      return;
    }

    if (missingFields.length > 0) {
      openSnackBar(`Please fill all required fields`, "error");
      return;
    }

    // ---- Submit Logic ----
    if (updateData?.id) {
      try {
        await updateConsultant({ id: updateData.id, ...payload }).unwrap();
        refetch();
        openSnackBar("Updated successfully");
        setTimeout(() => setShow(false), 350);
      } catch (error: any) {
        console.log(error, "error");
        if (error?.status === 400) {
          openSnackBar(error?.data?.error, "error");
        } else {
          setShow(false);
        }
      }
    } else {
      try {
        await addConsultant({ ...formData, activeStatus: "1" }).unwrap();
        refetch();
        openSnackBar("Created successfully");
        setTimeout(() => setShow(false), 350);
      } catch (error: any) {
        console.log(error, "error");
        if (error?.status === 400) {
          openSnackBar(error?.data?.error, "error");
        } else {
          setShow(false);
        }
      }
    }
  };

  const languageOptionsFormatted =
    languageOptions?.data?.map((language) => ({
      value: language.id,
      label: language.languagesName,
    })) || [];

  useEffect(() => {
    if (updateData && branchOptions) {
      setFormData({
        branchId: updateData.branchId || "",
        branchName: updateData.branchName || "",
        consultantName: updateData.consultantName || "",
        speciality: updateData.speciality || "",
        email: updateData.email || "",
        revenue: updateData.revenue || "",
        phone1: updateData.phone1 || "",
        phone2: updateData.phone2 || "",
        languagesOthers: updateData.languagesOthers || "",
        languagesSpokenId: updateData.languagesSpokenId || "",
        languages: updateData.languages || "",
        password: updateData.password || "",
      });

      const selectedBranch = branchOptions?.data?.find(
        (branch) => branch.id === updateData?.branchId
      );

      if (selectedBranch) {
        setFormData((prev) => ({
          ...prev,
          branchId: selectedBranch.id,
          branchName: selectedBranch.branchName,
        }));
      }

      const selectedLanguages =
        updateData.languagesSpokenId
          ?.split(",")
          .map((id) => {
            const language = languageOptions?.data?.find(
              (lang) => lang.id === parseInt(id)
            );
            return language
              ? { value: language.id, label: language.languagesName }
              : null;
          })
          .filter(Boolean) || [];

      setSelectedLanguages(selectedLanguages);
    }
  }, [updateData, branchOptions, languageOptions]);

  return (
    <div>
      <BackdropLoader loader={updateLoading || addLoading} />
      <Toast open={open} type={type} message={message} />
      {!view && (
        <div className="mt-3">
          <span className="f-18 fw-700">
            {updateData?.id ? "Edit Consultant" : "Add Consultant"}
          </span>
        </div>
      )}
      {ConsultantInput.map((a, i) => {
        if (a.name === "password" && updateData?.id) {
          return null;
        }
        if (a.role && a.role !== roleName) {
          return null;
        }
        return (
          <div className="input-custom mt-2" key={i}>
            {a.name === "branchName" && roleName !== "branch" ? (
              <div className="d-flex justify-content-between mt-2">
                <div>
                  <label className="det-label">Branch Name</label>
                </div>
                <div>
                  <select
                    className="input-custom"
                    name="branchName"
                    value={formData.branchId}
                    onChange={handleBranchChange}
                    disabled={view}
                  >
                    <option value="">Select Branch</option>
                    {branchOptions?.data?.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.branchName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : a.name === "languagesSpokenId" ? (
              <div className="d-flex justify-content-between mt-2">
                <div>
                  <label className="det-label">Languages</label>
                </div>
                <div className="input-custom">
                  <Select
                    isMulti
                    options={languageOptionsFormatted}
                    value={selectedLanguages}
                    onChange={handleLanguageChange}
                    isDisabled={view}
                  />
                </div>
              </div>
            ) : (
              <a.tag
                img={a.img}
                label={a.label}
                name={a.name}
                handleChange={handleChangeInfo}
                validation={a.validation}
                keyName={a.key}
                submit={true}
                disabled={view}
                value={formData[a.key]}
              />
            )}
          </div>
        );
      })}
      {!view && (
        <div className="d-flex justify-content-center mt-4 mb-3">
          <Button variant="contained" id="btn-save" onClick={handleSubmit}>
            {updateData?.id ? "Update Consultant" : "Add Consultant"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddConsultant;
